/**
 * Shared Heart Rate Zone Utilities
 *
 * Single source of truth for zone names, colors, boundary resolution,
 * and workout zone summary computation. All HR-zone-displaying components
 * should import from here instead of maintaining their own mappings.
 *
 * Supports both Cedar (Karvonen / HR Reserve) and Garmin (% Max HR) models.
 */

import type { Palette } from '../theme/palette';
import { expandPhasesForTimer } from './phaseGrouping';
import { computeBands } from './hrZoneBracketMath';
import hrZoneTables from './hrZoneTables.json';

// ─── Types ──────────────────────────────────────────────

export interface ZoneBoundaries {
  zone1Min: number;
  zone1Max: number;
  zone2Min: number;
  zone2Max: number;
  zone3Min: number;
  zone3Max: number;
  zone4Min: number;
  zone4Max: number;
  zone5Min: number;
  zone5Max: number;
  /** Friel-7 rows (positional user_heart_rate_zones.zone_6/zone_7 columns).
   *  Absent/NULL on 3/5-zone rows. */
  zone6Min?: number | null;
  zone6Max?: number | null;
  zone7Min?: number | null;
  zone7Max?: number | null;
  /** The row's declared band count (3/5/7) — mirrors
   *  user_heart_rate_zones.zone_count. Row count is derived from THIS,
   *  never from a hardcoded [1..5] (FE-8; a polarized athlete has 3 zones,
   *  a Friel athlete 7). Absent → 5 (legacy rows). */
  zoneCount?: number | null;
  zoneSystem?: string; // 'cedar' | 'garmin'
  maxHr?: number;
}

/** One band the classifier walks — mirror of the dict shape
 *  services/hr_zone_bands.py band helpers use. */
export interface ZoneBand {
  key: string; // 'zone_1'..'zone_7' (positional)
  lo: number;
  hi: number;
}

/** Sentinel for samples under the Zone-1 floor — a REAL bucket, never a
 *  silent discard (finding Z9). Mirror of services/hr_zone_bands.BELOW_ZONE_1. */
export const BELOW_ZONE_1 = 'below_zone_1';

export interface ZoneSummaryEntry {
  targetPercent: number;
  targetMinutes: number;
  range: string; // e.g. "128–145"
  zoneName: string;
}

export type HRZoneSummary = Record<string, ZoneSummaryEntry>;

export interface PhaseWithHR {
  target_hr_zone?: number;
  target_hr_min?: number;
  target_hr_max?: number;
  duration_seconds?: number;
  duration_minutes?: number;
  [key: string]: any;
}

// ─── Zone Names ─────────────────────────────────────────

const CEDAR_ZONE_NAMES: Record<number, string> = {
  1: 'Recovery',
  2: 'Endurance',
  3: 'Tempo',
  4: 'Threshold',
  5: 'VO2Max',
};

const GARMIN_ZONE_NAMES: Record<number, string> = {
  1: 'Recovery',
  2: 'Endurance',
  3: 'Tempo',
  4: 'Threshold',
  5: 'VO2Max',
};

/**
 * Get the display name for a zone number, respecting the user's zone system.
 */
export function getZoneName(
  zone: number,
  zoneSystem: string = 'cedar',
): string {
  const names =
    zoneSystem === 'garmin' ? GARMIN_ZONE_NAMES : CEDAR_ZONE_NAMES;
  return names[zone] || `Zone ${zone}`;
}

export type OwnZoneModel = 'polarized_3' | 'friel_7';

export const OWN_ZONE_MODELS: readonly OwnZoneModel[] = ['polarized_3', 'friel_7'];

export function ownZoneModel(zoneCount?: number | null): OwnZoneModel | null {
  return OWN_ZONE_MODELS.find((model) => hrZoneTables.model_zone_count[model] === zoneCount) ?? null;
}

export function ownZoneModelOf(zone: number, zoneCount?: number | null): OwnZoneModel | null {
  const model = ownZoneModel(zoneCount);
  return model && zone >= 1 && zone <= hrZoneTables.model_zone_count[model] ? model : null;
}

export function ownZonePosition(zone: number, zoneCount?: number | null): string {
  const model = ownZoneModelOf(zone, zoneCount);
  return model ? hrZoneTables.zone_position_labels[model][zone - 1] : `Zone ${zone}`;
}

export function ownZoneLabel(zone: number, zoneCount?: number | null): string {
  return ownZonePosition(zone, zoneCount).replace(/^Zone /, 'Z');
}

export function ownZoneName(zone: number, zoneSystem?: string, zoneCount?: number | null): string {
  const model = ownZoneModelOf(zone, zoneCount);
  return model ? hrZoneTables.zone_display_names[model][zone - 1] : getZoneName(zone, zoneSystem);
}

// ─── Zone Colors ────────────────────────────────────────

/**
 * Get the canonical color for a zone number.
 */
export function getZoneColor(palette: Palette, zone: number): string {
  return ({ 1: palette.data.hrZone.zone1, 2: palette.data.hrZone.zone2, 3: palette.data.hrZone.zone3, 4: palette.data.hrZone.zone4, 5: palette.data.hrZone.zone5 } as Record<number, string>)[zone] || palette.data.hrZone.zone1;
}

// ─── Zone Boundary Resolution ───────────────────────────

/** How many zones a ZoneBoundaries object can address (positional cap). */
const MAX_ZONE_INDEX = 7;

/**
 * Get the BPM range for a given zone number using the user's actual boundaries.
 *
 * Generic over the model's row count (FE-8): zones 6/7 resolve for Friel
 * rows instead of the old hardcoded 1..5 switch. Behaviour for zones 1–5
 * is unchanged (raw stored values pass through, as before).
 */
export function getZoneBPMRange(
  zone: number,
  boundaries: ZoneBoundaries,
): { min: number; max: number } {
  if (zone >= 1 && zone <= MAX_ZONE_INDEX) {
    const record = boundaries as unknown as Record<string, unknown>;
    const lo = record[`zone${zone}Min`];
    const hi = record[`zone${zone}Max`];
    if (typeof lo === 'number' || typeof hi === 'number') {
      return {
        min: typeof lo === 'number' ? lo : 0,
        max: typeof hi === 'number' ? hi : 0,
      };
    }
  }
  return { min: 0, max: 0 };
}

/**
 * Read a ZoneBoundaries object into ordered bands, honouring the row's
 * declared zoneCount (3/5/7) and skipping bands with missing bounds (a
 * polarized row carries NULL zone_4/5 — migration 1738).
 *
 * Mirror of services/hr_zone_bands.columns_to_bands.
 */
export function boundariesToBands(boundaries: ZoneBoundaries): ZoneBand[] {
  const record = boundaries as unknown as Record<string, unknown>;
  let count = 5;
  const declared = boundaries.zoneCount;
  if (typeof declared === 'number' && Number.isFinite(declared) && declared > 0) {
    count = Math.min(Math.floor(declared), MAX_ZONE_INDEX);
  }
  const bands: ZoneBand[] = [];
  for (let idx = 1; idx <= count; idx++) {
    const lo = record[`zone${idx}Min`];
    const hi = record[`zone${idx}Max`];
    if (
      typeof lo !== 'number' || !Number.isFinite(lo) ||
      typeof hi !== 'number' || !Number.isFinite(hi)
    ) {
      continue;
    }
    bands.push({ key: `zone_${idx}`, lo, hi });
  }
  return bands;
}

/**
 * Return the zone key a bpm belongs to, or BELOW_ZONE_1.
 *
 * Line-for-line mirror of services/hr_zone_bands.classify_bpm — ONE
 * convention, everywhere: `lo <= bpm < hi`, top band closed (`bpm >= lo`).
 * Samples under band 1's floor return BELOW_ZONE_1 — a real bucket, not a
 * silent discard (finding Z9). A bpm above the top band's hi — or inside a
 * seam gap of a legacy hand-built row — is still the top band (the SSOT's
 * documented catch-all). Change BOTH sides or neither.
 */
export function classifyBpm(bands: readonly ZoneBand[], bpm: number): string {
  if (!bands.length) return BELOW_ZONE_1;
  if (bpm < bands[0].lo) return BELOW_ZONE_1;
  const top = bands.length - 1;
  for (let idx = 0; idx < bands.length; idx++) {
    const { lo, hi } = bands[idx];
    if (idx === top) {
      if (bpm >= lo) return bands[idx].key;
    } else if (lo <= bpm && bpm < hi) {
      return bands[idx].key;
    }
  }
  // Above the top band's hi — still the top band (it is closed upward).
  return bands[top].key;
}

/**
 * Determine which zone a BPM value falls into using the user's actual boundaries.
 * Replaces all hardcoded threshold logic throughout the app.
 *
 * FE-8 / WS-D finding C8 — mirror of services/hr_zone_utils.get_zone_for_bpm.
 * Three things were wrong here, and each disagreed with the backend SSOT:
 *
 *   1. CLOSED upper bound (`bpm <= zoneNMax`) while the SSOT uses half-open
 *      `[lo, hi)` — every seam disagreed by a full zone.
 *   2. Hardcoded 5-zone walk — a polarized athlete's zone_4/5 are NULL, so
 *      every comparison was false and EVERY bpm fell through to…
 *   3. `return 5` — a "zone 5" a polarized athlete does not have; Friel's
 *      Z5b/Z5c both collapsed into 5.
 *
 * Now delegates to `classifyBpm`, the one convention. Sub-Z1 samples map to
 * 1 rather than the BELOW_ZONE_1 sentinel, because this function's contract
 * is an int zone number and its callers sum into zone buckets — exactly the
 * backend's documented int-contract decision.
 */
export function getZoneForBPM(
  bpm: number,
  boundaries: ZoneBoundaries,
): number {
  const bands = boundariesToBands(boundaries);
  if (bands.length > 0) {
    const key = classifyBpm(bands, bpm);
    if (key.startsWith('zone_')) {
      const parsed = parseInt(key.slice(5), 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    // Below the first band: attribute to zone 1 (int contract — see above).
    return 1;
  }
  // Degenerate row (no numeric bounds at all) — pre-FE-8 fallback,
  // byte-identical, mirroring the backend's own legacy fallback path.
  if (bpm <= boundaries.zone1Max) return 1;
  if (bpm <= boundaries.zone2Max) return 2;
  if (bpm <= boundaries.zone3Max) return 3;
  if (bpm <= boundaries.zone4Max) return 4;
  return 5;
}

export function phaseHrZone(
  phase: { target_hr_zone?: number | null; target_hr_min?: number | null; target_hr_max?: number | null },
  boundaries: ZoneBoundaries | null | undefined,
): number | undefined {
  if (phase.target_hr_zone) return phase.target_hr_zone;
  if (boundaries && phase.target_hr_min && phase.target_hr_max) {
    return getZoneForBPM(Math.round((phase.target_hr_min + phase.target_hr_max) / 2), boundaries);
  }
  return undefined;
}

/**
 * Classify a bpm against the DEFAULT %max 5-zone ladder — the shared
 * fallback for surfaces that have a max HR but no stored boundaries
 * (live timer, activity timeline). Bands come from the generated SSOT
 * tables via `computeBands` (FE-8: one source of band math on the FE),
 * classification through the one half-open convention above.
 */
export function getZoneForBPMPctMax(bpm: number, maxHr: number): number {
  const computed = computeBands({ method: 'pct_max', model: 'cedar_5', maxHr });
  if (!computed.length) return 1;
  const bands: ZoneBand[] = computed.map((b) => ({ key: b.num, lo: b.lo, hi: b.hi }));
  const key = classifyBpm(bands, bpm);
  if (key.startsWith('zone_')) {
    const parsed = parseInt(key.slice(5), 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 1;
}

// ─── Workout Zone Summary ───────────────────────────────

/**
 * Compute an aggregated zone distribution summary from an array of workout phases.
 *
 * Uses `target_hr_zone` (preferred) or resolves the zone from
 * `target_hr_min`/`target_hr_max` using the user's boundaries (fallback for old workouts).
 *
 * Phases with `repeat_count > 1` are expanded first (via `expandPhasesForTimer`)
 * so interval work AND inter-rep recovery both contribute to the total. Total
 * zone minutes will match the session's duration_minutes within ±2 (per-zone
 * rounding tolerance). Mirrors services/hr_zone_utils.py::compute_workout_zone_summary.
 *
 * Returns a dict keyed by `zone1`..`zone5` with target percent, minutes, and BPM range.
 */
/**
 * Phase-type → zone fallback. MIRRORS
 * services/hr_zone_utils.py::PHASE_TYPE_TO_ZONE_FALLBACK — the two are
 * asserted identical by tests/gate/test_faithful_card_grove.py, which parses
 * this literal. Any entry added on one side must be added on the other.
 *
 * Used ONLY for accounting (which zone does this phase's work belong to, for
 * the card's distribution summary). It never writes an HR target onto a
 * phase, so the prescription-axis contract is untouched.
 */
export const PHASE_TYPE_TO_ZONE_FALLBACK: Record<string, number> = {
  warmup: 1,
  cooldown: 1,
  recovery: 1,
  rest: 1,
  easy: 2,
  base: 2,
  steady_state: 2,
  endurance: 2,
  tempo: 3,
  progression: 3,
  threshold: 4,
  lt: 4,
  lactate_threshold: 4,
  vo2max: 5,
  interval: 5,
  sprint: 5,
  repetition: 5,
};

/**
 * RPE → zone. MIRRORS services/hr_zone_utils.py::_RPE_ZONE_BANDS — the two are
 * asserted identical by tests/gate/test_faithful_card_grove.py.
 *
 * Derived from the talk-test language of the canonical RPE_DESCRIPTIONS table
 * in services/intensity_prescription_templates.py (Borg CR-10):
 *   1-2 conversational -> Z1 · 3-4 sentences -> Z2 · 5-6 phrases/single words
 *   -> Z3 · 7-8 one-word/gasping -> Z4 · 9-10 all-out -> Z5
 */
export const RPE_ZONE_BANDS: ReadonlyArray<readonly [number, number]> = [
  [3.0, 1],
  [5.0, 2],
  [7.0, 3],
  [9.0, 4],
];

/** Map a Borg CR-10 `rpe_target` to its zone, or null. */
export function zoneForRpe(rpe: unknown): number | null {
  if (typeof rpe !== 'number' || !Number.isFinite(rpe)) return null;
  if (rpe <= 0 || rpe > 10) return null;
  for (const [ceiling, zone] of RPE_ZONE_BANDS) {
    if (rpe < ceiling) return zone;
  }
  return 5;
}

/** Map a `phase_type` to its canonical zone, or null when unrecognized. */
export function zoneForPhaseType(phaseType: unknown): number | null {
  if (typeof phaseType !== 'string') return null;
  const key = phaseType.trim().toLowerCase();
  return PHASE_TYPE_TO_ZONE_FALLBACK[key] ?? null;
}

export function computeWorkoutZoneSummary(
  phases: PhaseWithHR[],
  boundaries: ZoneBoundaries,
): HRZoneSummary | null {
  const zoneSeconds: Record<number, number> = {};
  let totalSeconds = 0;

  // Expand compact interval phases so repeats + recoveries are both counted.
  const expanded = expandPhasesForTimer(phases as any[]) as PhaseWithHR[];

  for (const phase of expanded) {
    // Determine zone
    let zone = phase.target_hr_zone;
    if (!zone || zone <= 0) {
      if (phase.target_hr_min && phase.target_hr_max) {
        // Symmetric band: use midpoint.
        const avgHr = Math.round(
          (phase.target_hr_min + phase.target_hr_max) / 2,
        );
        zone = getZoneForBPM(avgHr, boundaries);
      } else if (phase.target_hr_max) {
        // Ceiling-only (recovery_hr_target): "rest until HR drops below X".
        zone = getZoneForBPM(phase.target_hr_max, boundaries);
      } else if (phase.phase_type === 'recovery') {
        // Passive rest between intervals with no zone/BPM specified.
        // Default to Z1 so summary minutes align with the session's
        // duration_minutes (schema treats null recovery_target_hr_zone as
        // "passive rest," which is functionally Z1 territory).
        zone = 1;
      } else {
        // ── ACCOUNTING TIER (faithful-card-grove, 2026-08-25) ──
        // Mirrors services/hr_zone_utils.py. A pace/power/RPE-prescribed
        // phase carries no HR fields by design (the prescription-axis
        // contract), and before this tier it was indistinguishable from an
        // empty phase — so its minutes left BOTH numerator and denominator
        // and the card reported e.g. "Zone 1 — 100%" for a tempo session.
        // A phase with no recognizable `phase_type` still drops: that is
        // the genuine-cargo case, and it stays.
        // Prescription first, label second: `rpe_target` is what the athlete
        // was actually told to do; `phase_type` is only a name for the block.
        // The planner uses phase_type='interval' as a generic "main work"
        // label, so reading it alone rendered a Very Gentle run-walk (RPE 3)
        // as 51% Zone 5.
        const inferred =
          zoneForRpe((phase as any).rpe_target) ??
          zoneForPhaseType(phase.phase_type);
        if (inferred === null) {
          continue;
        }
        zone = inferred;
      }
    }

    const dur =
      phase.duration_seconds ||
      (phase.duration_minutes ? phase.duration_minutes * 60 : 0);
    if (dur <= 0) continue;

    zoneSeconds[zone] = (zoneSeconds[zone] || 0) + dur;
    totalSeconds += dur;
  }

  if (totalSeconds <= 0) return null;

  const summary: HRZoneSummary = {};
  // FE-8 / WS-H: iterate the athlete's DECLARED band count, not a hardcoded
  // [1..5]. Time accumulated in Friel's zone_6/zone_7 was counted into
  // totalSeconds above but never emitted here, so a Friel athlete's
  // percentages silently summed to less than 100 and their hardest work
  // vanished from the distribution entirely. Mirror of the identical loop in
  // services/hr_zone_utils.compute_workout_zone_summary.
  let declaredCount = 5;
  const declared = boundaries.zoneCount;
  if (typeof declared === 'number' && Number.isFinite(declared) && declared > 0) {
    declaredCount = Math.floor(declared);
  }
  for (let zoneNum = 1; zoneNum <= Math.max(declaredCount, 5); zoneNum++) {
    const secs = zoneSeconds[zoneNum];
    if (!secs) continue;

    const bpmRange = getZoneBPMRange(zoneNum, boundaries);
    const key = `zone${zoneNum}`;
    summary[key] = {
      targetPercent: Math.round((secs / totalSeconds) * 100),
      targetMinutes: Math.round(secs / 60),
      range: `${bpmRange.min}–${bpmRange.max}`,
      zoneName: getZoneName(zoneNum, boundaries.zoneSystem),
    };
  }

  return Object.keys(summary).length > 0 ? summary : null;
}
